import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy load components
const DashboardRouter = React.lazy(() => import('./components/common/DashboardRouter'));

// Auth pages - Lazy loaded
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const SellerRegistration = React.lazy(() => import('./pages/SellerRegistration'));
const RegistrationSuccess = React.lazy(() => import('./pages/RegistrationSuccess'));
const TestRegistration = React.lazy(() => import('./components/registration/TestRegistration'));
const SellerPhoneInput = React.lazy(() => import('./pages/SellerPhoneInput'));
const SellerOTPVerification = React.lazy(() => import('./pages/SellerOTPVerification'));

// Admin pages - Lazy loaded
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const SellerApproval = React.lazy(() => import('./pages/admin/SellerApproval'));
const ProductModeration = React.lazy(() => import('./pages/admin/ProductModeration'));
const CategoryManagement = React.lazy(() => import('./pages/admin/CategoryManagement'));
const OrderManagement = React.lazy(() => import('./pages/admin/OrderManagement'));
const Analytics = React.lazy(() => import('./pages/admin/Analytics'));
const AnalyticsRevenue = React.lazy(() => import('./pages/admin/AnalyticsRevenue'));
const AnalyticsSellers = React.lazy(() => import('./pages/admin/AnalyticsSellers'));
const AdminProfile = React.lazy(() => import('./pages/admin/AdminProfile'));

// Seller pages - Lazy loaded
const SellerDashboard = React.lazy(() => import('./pages/SellerDashboard'));
const ProductList = React.lazy(() => import('./pages/seller/ProductList'));
const AddProduct = React.lazy(() => import('./pages/seller/AddProduct'));
const EditProduct = React.lazy(() => import('./pages/seller/EditProduct'));
const SellerOrders = React.lazy(() => import('./pages/seller/OrdersComplete'));
const SellerProfile = React.lazy(() => import('./pages/seller/ProfileOptimized'));
const SellerAnalytics = React.lazy(() => import('./pages/seller/Analytics'));

// Common pages - Lazy loaded
const Profile = React.lazy(() => import('./pages/Profile'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/seller-phone-input" element={<SellerPhoneInput />} />
            <Route path="/seller-otp-verification" element={<SellerOTPVerification />} />
            <Route path="/test-registration" element={<TestRegistration />} />
            <Route path="/seller-registration" element={<SellerRegistration />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Default redirect based on role */}
            <Route index element={<Navigate to="/dashboard" replace />} />
            
            {/* Common routes */}
            <Route path="profile" element={<Profile />} />

            {/* Dashboard route - role-based */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute requiredRoles={['admin', 'seller']}>
                  <DashboardRouter />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/users"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/sellers"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/sellers/pending"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <SellerApproval />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/products"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <ProductModeration />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/categories"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <CategoryManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/orders"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <OrderManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/analytics"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/analytics/revenue"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AnalyticsRevenue />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/analytics/sellers"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AnalyticsSellers />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/categories/add"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <CategoryManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/profile"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AdminProfile />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="admin/settings"
              element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Profile />
                </ProtectedRoute>
              }
            />

            {/* Seller routes */}
            <Route
              path="seller/dashboard"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <SellerDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/products"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <ProductList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/products/add"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <AddProduct />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/products/:id/edit"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <EditProduct />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/products/low-stock"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <ProductList />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/orders/pending"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <SellerOrders />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/orders/completed"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <SellerOrders />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/analytics"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <SellerAnalytics />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/orders"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <SellerOrders />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="seller/profile"
              element={
                <ProtectedRoute requiredRoles={['seller']}>
                  <SellerProfile />
                </ProtectedRoute>
              }
            />
          </Route>

            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
    </AuthProvider>
  );
}

export default App;
